import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getPageTitle, getMetaDescription } from "../utils/pageTitle";
import {
  Box,
  SimpleGrid,
  Text,
  VStack,
  Image,
  Button,
  Badge,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Wrap,
  WrapItem,
  Container,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { SearchIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { FaGithub, FaExternalLinkAlt, FaFileAlt } from "react-icons/fa";
import { projects } from "./projects/projectData";
import { motion } from "framer-motion";

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);
const MotionSimpleGrid = motion(SimpleGrid);
const MotionInputGroup = motion(InputGroup);
const MotionHStack = motion(HStack);

const ProjectCard = ({ title, description, image, technologies, demoLink, githubLink, dissertationLink, categories, id, index }) => {
  const navigate = useNavigate();
  const bgColor = "brand.bg.card";
  const textColor = "brand.text.primary";
  const descriptionColor = "brand.text.secondary";

  // Use categories array if available, or fallback to an empty array
  const displayCategories = categories || [];

  return (
    <MotionBox
      bg={bgColor}
      borderRadius="lg"
      boxShadow="lg"
      overflow="hidden"
      backdropFilter="blur(10px)"
      _hover={{
        transform: "translateY(-8px)",
        boxShadow: "0 0 20px 5px rgba(49, 130, 206, 0.3)",
        transition: "all 0.3s ease-in-out",
      }}
      cursor="pointer"
      onClick={() => navigate(`/projects/${id}`)}
      height="100%"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        delay: index * 0.1, // Stagger based on index
      }}
    >
      <VStack 
        spacing={{ base: 2, md: 4 }}
        align="start" 
        height="100%" 
        p={{ base: 3, md: 5 }}
      >
        <Box
          position="relative"
          w="full"
          h={{ base: "250px", md: "250px" }}
          overflow="hidden"
          borderRadius="md"
          bg="gray.700"
        >
          <Image
            src={image}
            alt={title}
            w="full"
            h="full"
            objectFit="cover"
            transition="transform 0.3s ease-in-out"
            _groupHover={{ transform: "scale(1.05)" }}
          />
        </Box>
        <Wrap spacing={2}>
          {displayCategories.map((cat, idx) => (
            <WrapItem key={idx}>
              <Badge colorScheme="blue" fontSize="sm" px={2} py={1}>{cat}</Badge>
            </WrapItem>
          ))}
        </Wrap>
        <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color={textColor} _hover={{ color: "blue.500" }}>
          {title}
        </Text>
        <Text color={descriptionColor} noOfLines={3} fontSize={{ base: "sm", md: "md" }}>{description}</Text>
        <Wrap spacing={2}>
          {technologies.map((tech, index) => (
            <WrapItem key={index}>
              <Badge colorScheme="green" fontSize="xs" px={2} py={1}>{tech}</Badge>
            </WrapItem>
          ))}
        </Wrap>
        <HStack 
          spacing={{ base: 2, md: 4 }} 
          mt="auto" 
          flexWrap={{ base: "wrap", md: "nowrap" }} 
          w="full" 
          justify="flex-start"
        >
          {demoLink && (
            <Button
              as="a"
              href={demoLink}
              target="_blank"
              leftIcon={<FaExternalLinkAlt />}
              colorScheme="blue"
              variant="outline"
              size={{ base: "sm", md: "md" }}
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "md",
                transition: "all 0.2s ease-in-out",
              }}
              onClick={(e) => e.stopPropagation()}
              mb={{ base: 2, md: 0 }}
              flex={{ base: "1 0 100%", md: "initial" }}
            >
              Live Demo
            </Button>
          )}
          {githubLink && (
            <Button
              as="a"
              href={githubLink}
              target="_blank"
              leftIcon={<FaGithub />}
              colorScheme="gray"
              variant="outline"
              size={{ base: "sm", md: "md" }}
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "md",
                transition: "all 0.2s ease-in-out",
              }}
              onClick={(e) => e.stopPropagation()}
              mb={{ base: 2, md: 0 }}
              flex={{ base: "1 0 100%", md: "initial" }}
            >
              GitHub
            </Button>
          )}
          {dissertationLink && (
            <Button
              as="a"
              href={dissertationLink}
              target="_blank"
              leftIcon={<FaFileAlt />}
              colorScheme="purple"
              variant="outline"
              size={{ base: "sm", md: "md" }}
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "md",
                transition: "all 0.2s ease-in-out",
              }}
              onClick={(e) => e.stopPropagation()}
              flex={{ base: "1 0 100%", md: "initial" }}
            >
              Dissertation
            </Button>
          )}
        </HStack>
      </VStack>
    </MotionBox>
  );
};

const ProjectsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  // Extract all unique categories from projects
  const allCategories = [...new Set(projects.flatMap(project => project.categories || []))].sort();
  const categories = ["all", ...allCategories];

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Check if any of the project's categories match the selected filter
    const projectCategories = project.categories || [];
    const matchesCategory = categoryFilter === "all" || projectCategories.includes(categoryFilter);
    
    return matchesSearch && matchesCategory;
  });

  const getCategoryDisplayName = (value) => {
    return value === "all" ? "All Categories" : value;
  };

  return (
    <Box 
      pt={{ base: 32, md: 32 }}
      pb={{ base: 20, md: 24 }}
      px={4} 
      bg="brand.primary" 
      minH="100vh"
      position="relative"
      overflow="hidden"
    >      <Helmet>
        <title>{getPageTitle("Projects")}</title>
        <meta name="description" content={getMetaDescription("Explore my projects, including web development, data science, and more.")} />
      </Helmet>
      
      {/* Background decoration elements - similar to homepage */}
      <Box
        position="absolute"
        top="5%"
        left="20%"
        w="300px"
        h="300px"
        borderRadius="full"
        bg="brand.bg.hover"
        filter="blur(80px)"
        opacity="0.1"
        zIndex="0"
      />
      <Box
        position="absolute"
        bottom="10%"
        right="5%"
        w="250px"
        h="250px"
        borderRadius="full"
        bg="rgba(79, 209, 197, 0.2)"
        filter="blur(100px)"
        opacity="0.15"
        zIndex="0"
      />
      
      {/* Additional color splashes */}
      <Box
        position="absolute"
        top="25%"
        right="15%"
        w="180px"
        h="180px"
        borderRadius="full"
        bgGradient="linear(to-r, purple.400, blue.300)"
        filter="blur(90px)"
        opacity="0.07"
        zIndex="0"
      />
      <Box
        position="absolute"
        bottom="30%"
        left="10%"
        w="220px"
        h="220px"
        borderRadius="full"
        bgGradient="linear(to-br, pink.300, red.200)"
        filter="blur(100px)"
        opacity="0.06"
        zIndex="0"
      />
      
      <Container maxW="container.xl" position="relative" zIndex="1">
        <MotionVStack 
          spacing={12} 
          align="stretch"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            alignSelf="center"
          >
            <Text 
              fontSize={{ base: "3xl", md: "4xl" }}
              fontWeight="bold" 
              color="white" 
              textAlign="center"
              position="relative"
              display="inline-block"
              pb={4}
              _after={{
                content: '""',
                position: "absolute",
                bottom: 0,
                left: "50%",
                transform: "translateX(-50%)",
                width: { base: "60px", md: "80px" },
                height: "4px",
                bg: "white",
                borderRadius: "2px"
              }}
            >
              All Projects
            </Text>
          </MotionBox>
          
          <MotionHStack 
            spacing={1} 
            w="full" 
            alignItems="center"
            flexWrap="nowrap"
            justifyContent={{ base: "flex-start", md: "center" }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <MotionInputGroup 
              maxWidth={{ base: "none", md: "400px" }} 
            >
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg="rgba(255, 255, 255, 0.35)"
                color="white"
                borderRadius="md"
                borderColor="rgba(255, 255, 255, 0.2)"
                _placeholder={{ color: 'gray.400' }}
                _hover={{ bg: "rgba(255, 255, 255, 0.4)" }}
                _focus={{ bg: "rgba(255, 255, 255, 0.4)", borderColor: "blue.500", boxShadow: "none" }}
              />
            </MotionInputGroup>
            
            <Menu>
              <MenuButton
                as={Button}
                bg="rgba(255, 255, 255, 0.35)"
                color="white"
                borderRadius="md"
                borderWidth="1px"
                borderColor="rgba(255, 255, 255, 0.2)"
                _hover={{ bg: "rgba(255, 255, 255, 0.4)" }}
                _active={{ bg: "rgba(255, 255, 255, 0.45)" }}
                minWidth={{ base: "140px", md: "200px" }} 
                textAlign="left"
                fontWeight="normal"
                rightIcon={<ChevronDownIcon />}
                flexShrink={0}
              >
                {getCategoryDisplayName(categoryFilter)}
              </MenuButton>
              <MenuList 
                bg="rgba(45, 55, 72, 0.95)"
                borderColor="rgba(74, 85, 104, 0.9)"
                backdropFilter="blur(5px)"
                minWidth="200px"
              >
                {categories.map((category) => (
                  <MenuItem 
                    key={category} 
                    onClick={() => setCategoryFilter(category)}
                    bg={category === categoryFilter ? "rgba(66, 153, 225, 0.2)" : "transparent"}
                    color="white"
                    _hover={{md: { bg: "rgba(66, 153, 225, 0.8)" } }}
                    _focus={{md: { bg: "rgba(49, 130, 206, 0.8)", boxShadow: "outline" }}}
                  >
                    {getCategoryDisplayName(category)}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </MotionHStack>

          <MotionSimpleGrid 
            columns={{ base: 1, md: 2, lg: 3 }} 
            spacing={10} 
            w="full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {filteredProjects.map((project, index) => (
              <ProjectCard key={project.id} {...project} index={index} />
            ))}
          </MotionSimpleGrid>
        </MotionVStack>
      </Container>
    </Box>
  );
};

export default ProjectsPage;